import React, { BaseSyntheticEvent, FC, useEffect, useState } from 'react';
import { ReactComponent as TrashIcon } from '../../assets/icons/trashcan-icon.svg';
import TwField, { StyledTextfieldLabel, TwInputTypes } from '../TwFields/TwFields';
import Popper from '@mui/material/Popper';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import IconButton from '@mui/material/IconButton';
import { TAG_COLOR, TagsPalette } from '../../redux/api/loadTags/loadTags.model';
import { useEditTagMutation, useTagByIdQuery } from '../../redux/api/loadTags/loadTagsApi';
import CheckIcon from '@mui/icons-material/Check';
import useNotifications from '../../providers/Notifications/Notifications.provider';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { ClickAwayListener, Theme, useMediaQuery } from '@mui/material';
import { setTripDetailReload } from '../../redux/features/navigation/navigationSlice';

interface TwEditDeleteTagProps {
  open: boolean;
  anchor: any;
  tagId: string;
  orderId: string;
  handleClickDelete: () => void;
  handleClose: () => void;
  reloadData?: () => void;
}

const TwEditDeleteTag: FC<TwEditDeleteTagProps> = ({
  open,
  anchor,
  tagId,
  orderId,
  handleClickDelete,
  handleClose,
  reloadData
}): JSX.Element => {
  const dispatch = useAppDispatch();
  const { data: tag, isFetching } = useTagByIdQuery(tagId, { skip: !tagId });
  const filterParams = useAppSelector((state) => state.activeTrips.filters);
  const isTripDetailOpen = useAppSelector((state) => state.navigation.isTripDetailOpen);
  const [name, setName] = useState<string>('');
  const [color, setColor] = useState<string>('');
  const matchesMobileOrTablet = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));

  const { addNotification } = useNotifications();
  const [editTag] = useEditTagMutation();

  useEffect(() => {
    if (open && !isFetching) {
      if (tag === null) {
        return addNotification({
          message: 'Tag was not found, can not be edited',
          variant: 'error'
        });
      }
      if (tag !== undefined && tag !== null) {
        setName(tag.name);
        setColor(tag.color);
      }
    }
  }, [tag, open]);

  const onClose = (): void => {
    setName('');
    setColor('');
    handleClose();
  };

  const handleEdit = (colorPicker?: string): void => {
    if (tag) {
      editTag({
        tagId: tag.id,
        name: name,
        color: colorPicker ? (colorPicker as TAG_COLOR) : (color as TAG_COLOR),
        orderId: orderId,
        searchTripParams: {
          filters: {
            ...filterParams,
            includeArchived: matchesMobileOrTablet
          }
        }
      })
        .unwrap()
        .then(() => {
          setTimeout(() => {
            dispatch(setTripDetailReload(true));

            reloadData && reloadData();
          }, 500);
          onClose();
        });
    }
  };

  return (
    <ClickAwayListener onClickAway={onClose}>
      <Popper
        disablePortal
        open={open}
        className="load-tags__edit-popper"
        anchorEl={anchor}
        placement="bottom-start"
        onClick={(e): void => e.stopPropagation()}
      >
        <Box className="load-tags__edit-popper__textfield">
          <TwField
            name="edit-tag"
            label="Tag name"
            value={name}
            onInputChange={(e): void => {
              setName(e.target.value);
            }}
            inputType={TwInputTypes.text}
            fullWidth
            onKeyDown={(event): void => {
              if (event.key === 'Enter' && name !== '') {
                handleEdit();
              }
            }}
          />
          <IconButton onClick={handleClickDelete}>
            <TrashIcon />
          </IconButton>
        </Box>
        <Box className="load-tags__edit-popper__colors-container">
          <StyledTextfieldLabel>Colors</StyledTextfieldLabel>
          <Stack flexDirection={'row'} gap={1}>
            {Object.entries(TagsPalette).map((pickercolor) => {
              return (
                <Box
                  key={pickercolor[0]}
                  id={pickercolor[0]}
                  onClick={(e: BaseSyntheticEvent): void => {
                    handleEdit(e.target.id);
                  }}
                  className={`load-tags__edit-popper__color-pick ${
                    color === pickercolor[0] ? 'load-tags__selected-color-picker' : ''
                  }`}
                  sx={{
                    backgroundColor: pickercolor[1].picker,
                    boxShadow:
                      color === pickercolor[0] ? `0 0 0 2px ${pickercolor[1].picker}` : 'none'
                  }}
                >
                  {color === pickercolor[0] && <CheckIcon />}
                </Box>
              );
            })}
          </Stack>
        </Box>
      </Popper>
    </ClickAwayListener>
  );
};

export default TwEditDeleteTag;
